// Non-blocking banner that appears at the top of every authenticated page
// when the user signed up with email/password and has not yet verified their email.
import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Mail, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const EmailVerificationBanner = ({ user, onDismiss }) => {
  const [sending, setSending] = useState(false);
  const [hidden, setHidden] = useState(false);

  // Only show for password-auth users who haven't verified yet
  if (!user || hidden) return null;
  if (user.email_verified) return null;
  if (user.auth_method && user.auth_method !== "password") return null;

  const handleResend = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/auth/resend-verification`, {}, { withCredentials: true });
      if (r.data?.already_verified) {
        toast.success("Your email is already verified — refresh to update.");
        return;
      }
      if (r.data?.sent) {
        toast.success("Verification email sent. Check your inbox.");
      } else {
        toast.warning("Verification email queued — if it doesn't arrive within a minute, contact your administrator.");
      }
    } catch {
      toast.error("Couldn't resend verification email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-3 text-sm"
      data-testid="verify-email-banner"
    >
      <Mail className="w-4 h-4 text-amber-700 flex-shrink-0" />
      <div className="flex-1 text-amber-900">
        <span className="font-medium">Verify your email.</span>{" "}
        <span className="text-amber-800">We sent a confirmation link to <span className="font-mono">{user.email}</span>.</span>
      </div>
      <button
        onClick={handleResend}
        disabled={sending}
        className="text-xs px-2 py-1 bg-amber-700 hover:bg-amber-800 text-white rounded inline-flex items-center gap-1 disabled:opacity-60"
        data-testid="verify-resend-btn"
      >
        {sending ? <><Loader2 className="w-3 h-3 animate-spin" /> Sending…</> : "Resend"}
      </button>
      <button
        onClick={() => { setHidden(true); if (onDismiss) onDismiss(); }}
        className="text-amber-700 hover:text-amber-900"
        aria-label="Dismiss"
        data-testid="verify-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default EmailVerificationBanner;
